import { type FormikProps } from 'formik'

import { useProcurifyIntl } from '@webapp/hooks'
import {
  ExpenseReportForm,
  type ExpenseReportFormSchema,
} from '@webapp/modules/expense/components/ExpenseReportForm'
import { type Expense } from '@webapp/modules/expense/entities/Expense'
import { type ExpenseItem } from '@webapp/modules/expense/entities/ExpenseItem'
import messages from './messages'
import { useReviseExpenseReportPresenter } from './useReviseExpenseReportPresenter'

export interface IReviseExpenseReportPresenterProps {
  expense: Expense
  initialValues?: Partial<ExpenseReportFormSchema>
  formRef?: React.MutableRefObject<FormikProps<ExpenseReportFormSchema> | null>
  onSuccess: () => void
  onEditItem: (item: ExpenseItem) => void
  onSubmittingChange?: (isSubmitting: boolean) => void
  onCancel: () => void
}

export const ReviseExpenseReportPresenter = ({
  expense,
  initialValues,
  formRef,
  onSuccess,
  onEditItem,
  onSubmittingChange,
  onCancel,
}: IReviseExpenseReportPresenterProps) => {
  const intl = useProcurifyIntl()
  const {
    states: { isSubmitting, selectedLocationId, selectedDepartmentId },
    methods: { handleSubmit, handleLocationChange, handleDepartmentChange },
  } = useReviseExpenseReportPresenter({
    expense,
    initialValues,
    onSuccess,
    onSubmittingChange,
  })

  return (
    <ExpenseReportForm
      expense={expense}
      initialValues={initialValues}
      canEditLocation={true}
      canEditDepartment={true}
      canEditTravelTicket={true}
      canEditNextApprover={true}
      statusBadges={[
        { type: 'error', text: intl.formatMessage(messages.deniedBadge) },
        { type: 'warning', text: intl.formatMessage(messages.revisingBadge) },
      ]}
      isSubmitting={isSubmitting}
      submitLabel={intl.formatMessage(messages.resubmitButton)}
      formRef={formRef}
      onSubmit={handleSubmit}
      onEditItem={onEditItem}
      onCancel={onCancel}
      onLocationChange={handleLocationChange}
      onDepartmentChange={handleDepartmentChange}
      selectedLocationId={selectedLocationId}
      selectedDepartmentId={selectedDepartmentId}
    />
  )
}
